import { useTranslation } from 'react-i18next'
import { MessageSquare, BookOpen } from 'lucide-react'
import type { Investigation, InvestigationReadLead } from '@/domains/types'
import type { SupportedLangCode } from '@/application/i18n/language'
import { useGetCommentsByInvestigation } from '@/application/services/comment.service'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { CollapsibleSection } from '@/ui/components/common/CollapsibleSection'
import { ReadModeToolbar } from './ReadModeToolbar'
import { ShareButtons } from './ShareButtons'
import { hasContent } from './hasContent'
import { estimateReadingMinutes } from './readingTime'
import { ReadAloudPlayer } from './audio/ReadAloudPlayer'

/**
 * Article-style masthead, laid out in the order a newspaper front page uses:
 * kicker (the categories, as a tracked sans rule-line rather than UI badges),
 * the investigation's single `<h1>` in the display serif, the byline between
 * hairlines, the cover image, then the description — whose first paragraph the
 * `read-standfirst` class promotes to a standfirst (chapô).
 *
 * Intentionally lighter than `InvestigationHeaderSection` — no team modal, no
 * editable conclusion selector — read mode is a reading surface, not a
 * management view.
 */
export function ReadArticleHeader({
  investigation,
  leads,
  pageUrl,
  availableLocales,
}: {
  investigation: Investigation
  leads: InvestigationReadLead[]
  /** Absolute, localized article URL — passed down to `ShareButtons`. */
  pageUrl: string
  /** Locales the whole investigation subtree is fully translated into —
   * passed straight through to `ReadAloudPlayer`'s language resolution. */
  availableLocales: SupportedLangCode[]
}) {
  const { t } = useTranslation()
  const readingMinutes = estimateReadingMinutes(investigation, leads)
  // Shares its query cache with `ArticleCommentsSection` (same query key), so
  // this doesn't cost a second request — only the count of what every
  // visitor actually sees, regardless of their own pending/moderator-visible
  // comments, which the API may also include in this same response.
  const { data: comments } = useGetCommentsByInvestigation(investigation.id)
  const approvedCommentsCount = (comments ?? []).filter(
    (comment) => comment.status === 'approved',
  ).length

  return (
    <header className="mb-12 pb-8 border-b border-border">
      <div className="flex items-start justify-between gap-4">
        {investigation.categoryTerms && investigation.categoryTerms.length > 0 ? (
          <p className="font-sans text-sm font-semibold uppercase tracking-[0.18em] text-primary">
            {investigation.categoryTerms.map((term) => term.label).join(' • ')}
          </p>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2 shrink-0">
          <ShareButtons title={investigation.name} pageUrl={pageUrl} />
          <ReadModeToolbar />
        </div>
      </div>

      {/*
        The headline steps down on narrow screens: 68px is right for a desktop
        masthead and unreadable on a phone, where it breaks every two words.
        `text-balance` evens out the line lengths instead of leaving a single
        orphaned word on the last line.
      */}
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-foreground leading-[1.08] tracking-[-0.015em] text-balance mb-6">
        {investigation.name}
      </h1>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-y border-border py-3 font-sans text-sm text-muted-foreground mb-8">
        {investigation.author && (
          <>
            <span className="uppercase tracking-wider">
              {t('read_mode.by')}{' '}
              <span className="text-foreground font-semibold">{investigation.author}</span>
            </span>
            <span aria-hidden="true" className="text-border">
              |
            </span>
          </>
        )}
        <time dateTime={new Date(investigation.createdAt).toISOString()}>
          {new Date(investigation.createdAt).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })}
        </time>
        <span aria-hidden="true" className="text-border">
          |
        </span>
        <span className="flex items-center gap-1.5">
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          {t('read_mode.reading_time', { count: readingMinutes })}
        </span>
        <a
          href="#comments"
          className="ml-auto flex items-center gap-1.5 uppercase tracking-wider text-primary hover:underline"
        >
          <MessageSquare className="h-4 w-4" aria-hidden="true" />
          {approvedCommentsCount > 0 && approvedCommentsCount} {t('read_mode.comments_link')}
        </a>
      </div>

      <ReadAloudPlayer
        investigation={investigation}
        leads={leads}
        availableLocales={availableLocales}
      />

      {investigation.imageUrl && (
        <figure className="mb-8">
          <div className="rounded-lg overflow-hidden">
            <img
              src={investigation.imageUrl}
              alt={investigation.name}
              className="w-full object-cover max-h-[500px]"
            />
          </div>
        </figure>
      )}

      {hasContent(investigation.description) && (
        <div className="read-standfirst">
          <MarkdownView content={investigation.description} variant="article" />
        </div>
      )}

      {hasContent(investigation.notes) && (
        <CollapsibleSection title={t('read_mode.notes_label')} className="mt-6">
          <MarkdownView content={investigation.notes ?? ''} variant="article" />
        </CollapsibleSection>
      )}
    </header>
  )
}
